/**
 * Load Stripe.js on demand.
 *
 * It used to be a `<script>` tag in public/index.html, so it ran on every page
 * of the site — the landing page, the legal pages, the community forum — for
 * every visitor, signed in or not, before any consent, and set `__stripe_mid`
 * for a year. Stripe's own docs recommend site-wide loading for fraud signal
 * quality, but that is Stripe's interest, and it is not a basis for putting a
 * third-party cookie on someone who is reading our privacy policy.
 *
 * Now it loads the first time something actually needs it: a checkout, the
 * saved-payment-methods page, or payout onboarding. Those are places where a
 * payment relationship exists and the cookie is genuinely essential.
 *
 * Idempotent: a second call while the first is in flight shares the same
 * promise, and once `window.Stripe` exists nothing is injected again.
 */

const STRIPE_JS_SRC = 'https://js.stripe.com/v3/';

let pending = null;

export const ensureStripeLoaded = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Stripe.js can only be loaded in the browser'));
  }
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (pending) return pending;

  pending = new Promise((resolve, reject) => {
    // Another path may have injected the tag already (e.g. a fast back/forward
    // between two payment pages). Reuse it rather than adding a second one.
    const existing = document.querySelector(`script[src="${STRIPE_JS_SRC}"]`);
    const script = existing || document.createElement('script');

    const onLoad = () => {
      if (window.Stripe) {
        resolve(window.Stripe);
      } else {
        pending = null;
        reject(new Error('Stripe.js loaded but window.Stripe is missing'));
      }
    };
    const onError = () => {
      pending = null;
      reject(new Error('Stripe.js failed to load'));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    if (!existing) {
      script.src = STRIPE_JS_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return pending;
};
